import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { CheckCircle, Circle, Upload, ArrowRight, Lock } from 'lucide-react';
import type { LessonProgress } from '@/types';
import { EvidenceUploadDialog } from './evidence-upload-dialog';
import { ContentViewer } from './content-viewer';
import { ObservationLessonState } from '@/components/observation/observation-lesson-state';
import { LessonConfirmPanel } from './_components/lesson-confirm-panel';
import {
  LESSON_ICONS,
  LESSON_TYPE_LABELS,
  VIEWABLE_TYPES,
  VIEW_LABELS,
  READING_TYPES,
} from './lesson-row.constants';

export function LessonRow({
  lesson,
  isNext,
  onComplete,
  isCompleting,
  minVideoPercent,
  hasInstructor,
  enrollmentId,
}: {
  lesson: LessonProgress;
  isNext?: boolean;
  onComplete: () => void;
  isCompleting: boolean;
  /** Minimum % of video watched to allow completion (0 or undefined = no requirement). */
  minVideoPercent?: number;
  /** Whether the course has at least one instructor assigned. */
  hasInstructor?: boolean;
  /** Enrollment ID — required for practical_observation to drive request state. */
  enrollmentId: number;
}) {
  const TypeIcon = LESSON_ICONS[lesson.type];
  const [showUpload, setShowUpload] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [hasOpenedMaterial, setHasOpenedMaterial] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [readingConfirmed, setReadingConfirmed] = useState(false);
  const [hasScrolledContent, setHasScrolledContent] = useState(false);
  const [videoWatchPercent, setVideoWatchPercent] = useState(0);
  const rowRef = useRef<HTMLLIElement>(null);
  const requiresReadingConfirmation = READING_TYPES.has(lesson.type);
  const requiresVideoWatch = lesson.type === 'video' && !!minVideoPercent && minVideoPercent > 0;
  const videoMet = !requiresVideoWatch || videoWatchPercent >= (minVideoPercent ?? 0);

  useEffect(() => {
    if (isNext && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isNext]);

  const canView = (lesson.content_url || lesson.content_body) && VIEWABLE_TYPES.has(lesson.type);
  const viewLabel = VIEW_LABELS[lesson.type] ?? 'Ver';
  // Kattia: review the material first, then take the quiz. When the lesson has
  // viewable material, the quiz stays locked until it has been opened once.
  const canTakeQuiz = !canView || hasOpenedMaterial;

  return (
    <>
      <li
        ref={isNext ? rowRef : undefined}
        className={cn(
          'flex items-center gap-3 px-5 py-3',
          isNext && 'border-l-3 border-l-primary bg-primary/5',
        )}
      >
        {/* Completion status icon */}
        {lesson.is_completed ? (
          <CheckCircle className="h-5 w-5 shrink-0 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
        )}

        {/* Lesson type icon + title */}
        <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cn(
                'text-sm',
                lesson.is_completed
                  ? 'text-muted-foreground line-through'
                  : 'font-medium text-card-foreground',
              )}
            >
              {lesson.title}
            </p>
            {isNext && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Siguiente <ArrowRight className="h-3 w-3" />
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {LESSON_TYPE_LABELS[lesson.type]}
            {lesson.has_quiz && ' + Evaluación'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-2">
          {canView && (
            <button
              type="button"
              onClick={() => {
                setShowViewer(true);
                setHasOpenedMaterial(true);
              }}
              className="rounded-md border border-border px-3 py-1 text-xs font-medium text-card-foreground transition-colors hover:bg-accent"
            >
              {viewLabel}
            </button>
          )}

          {lesson.has_quiz &&
            lesson.quiz_id &&
            !lesson.is_completed &&
            (canTakeQuiz ? (
              <Link
                to={`/quiz/${lesson.quiz_id}`}
                className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Tomar Quiz
              </Link>
            ) : (
              <button
                type="button"
                disabled
                title="Primero revisa el material de la lección"
                className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-medium text-muted-foreground opacity-60"
              >
                <Lock className="h-3 w-3" />
                Tomar Quiz
              </button>
            ))}

          {lesson.type === 'evidence_upload' && !lesson.is_completed && (
            <button
              type="button"
              onClick={() => setShowUpload(true)}
              className="rounded-md bg-amber-500 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-amber-600"
            >
              <Upload className="mr-1 inline h-3 w-3" />
              Subir archivo
            </button>
          )}

          {lesson.type === 'practical_record' &&
            !lesson.is_completed &&
            (hasInstructor ? (
              <span className="rounded-md border border-green-300 bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                Evaluado por instructor
              </span>
            ) : (
              <span className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Requiere instructor
              </span>
            ))}

          {lesson.type === 'practical_observation' && (
            <span className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
              Observación práctica
            </span>
          )}

          {!lesson.has_quiz &&
            lesson.type !== 'evidence_upload' &&
            lesson.type !== 'practical_record' &&
            lesson.type !== 'practical_observation' &&
            !lesson.is_completed && (
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={isCompleting || showConfirm}
                className={cn(
                  'rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90',
                  (isCompleting || showConfirm) && 'cursor-not-allowed opacity-60',
                )}
              >
                {isCompleting ? 'Guardando...' : 'Completar'}
              </button>
            )}
        </div>
      </li>

      {lesson.type === 'practical_observation' && (
        <li className="border-t border-border px-5 py-3">
          <ObservationLessonState
            enrollmentId={enrollmentId}
            lessonId={lesson.id}
            isLocked={!isNext && !lesson.is_completed}
          />
        </li>
      )}

      {/* Confirmation panel */}
      {showConfirm && (
        <LessonConfirmPanel
          lesson={lesson}
          isCompleting={isCompleting}
          requiresReadingConfirmation={requiresReadingConfirmation}
          requiresVideoWatch={requiresVideoWatch}
          videoMet={videoMet}
          minVideoPercent={minVideoPercent}
          videoWatchPercent={videoWatchPercent}
          readingConfirmed={readingConfirmed}
          onReadingConfirmedChange={setReadingConfirmed}
          hasScrolledContent={hasScrolledContent}
          onConfirm={() => {
            onComplete();
            setShowConfirm(false);
            setReadingConfirmed(false);
          }}
          onClose={() => {
            setShowConfirm(false);
            setReadingConfirmed(false);
          }}
        />
      )}

      {showUpload && (
        <EvidenceUploadDialog
          lessonId={lesson.id}
          onClose={() => setShowUpload(false)}
          onCompleted={onComplete}
        />
      )}
      {showViewer && (lesson.content_url || lesson.content_body) && (
        <ContentViewer
          url={lesson.content_url ?? ''}
          htmlContent={lesson.content_body ?? undefined}
          title={lesson.title}
          onClose={() => setShowViewer(false)}
          onScrolledToEnd={
            requiresReadingConfirmation && !hasScrolledContent
              ? () => setHasScrolledContent(true)
              : undefined
          }
          onVideoProgress={requiresVideoWatch ? setVideoWatchPercent : undefined}
        />
      )}
    </>
  );
}
